import { TeamInvitation } from 'api/models';
import { Searchbar } from 'components/design-system/Searchbar';
import BaseTable from 'components/design-system/Table/BaseTable';
import { SyntheticEvent, useState } from 'react';
import { useDebounce } from 'react-use';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { capitalizeString } from 'utils/formatString';
import { ExpiryDisplay, getHumanReadableDate } from 'utils/subscription_plan';
import { Actions } from 'components/design-system';
import { InviteTeamMemberDialog } from 'components/dialogs';
import { DeleteInvitationDialog } from './DeleteInvitationDialog';

interface TeamPendingMembersTabProps {
  pendingMembers: TeamInvitation[];
  refetchPendingMembers: VoidFunction;
}

export default function TeamsPendingMembers({
  pendingMembers,
  refetchPendingMembers,
}: TeamPendingMembersTabProps) {
  const { logEvent } = AnalyticsEventLogger();
  const [searchInvitation, setSearchInvitation] = useState<string>('');
  const [currentInvitation, setCurrentInvitation] = useState<
    TeamInvitation | undefined
  >(undefined);
  const [dialogOpen, setDialogOpen] = useState<'resend' | 'cancel' | undefined>(
    undefined,
  );
  useDebounce(
    () => {
      if (searchInvitation) {
        logEvent('search-member', { searchMember: searchInvitation });
      }
    },
    1000,
    [searchInvitation],
  );

  const handleOnChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchInvitation(newValue);
  };

  const membersToShow =
    searchInvitation !== ''
      ? pendingMembers.filter((item) =>
          item?.email.toLowerCase().includes(searchInvitation.toLowerCase()),
        )
      : pendingMembers;

  const membersToShowSorted = Object.values(membersToShow).sort(
    (a, b) => (b.expiry || 0) - (a.expiry || 0),
  );

  return (
    <>
      <div className="w-full items-center justify-end flex">
        {pendingMembers?.length ? (
          <Searchbar onChange={handleOnChange} className="pr-[-10px]" />
        ) : (
          <></>
        )}
      </div>
      {pendingMembers?.length ? (
        <BaseTable
          settings={{
            localStorageKey: 'teams_pending_members_table',
            dataCyPrefix: 'pending-members',
          }}
          data={membersToShowSorted || []}
          noMatchingData={membersToShowSorted?.length === 0}
          columns={[
            {
              id: '1',
              title: 'Email',
              titleStyle: { width: '25%' },
              cellType: 'text-cell',
              hideCapture: true,
              render: (row) => {
                return `${row?.email}`;
              },
            },
            {
              id: '2',
              title: 'Inviter',
              titleStyle: { width: '25%' },
              cellType: 'text-cell',
              hideCapture: true,
              render: (row) => {
                return `${row?.inviter}`;
              },
            },
            {
              id: '3',
              title: 'Status',
              titleStyle: { width: '20%' },
              cellType: 'badge-cell',
              cellProperties: {
                variant: (row) => {
                  return ExpiryDisplay(row?.expiry) !== ''
                    ? 'error'
                    : 'neutral';
                },
              },
              render: (row) => {
                return ExpiryDisplay(row?.expiry) !== ''
                  ? 'Expired'
                  : capitalizeString(row?.status);
              },
            },
            {
              id: '4',
              title: 'Expiry',
              titleStyle: { width: '20%' },
              cellType: 'text-cell',
              render: (row) => {
                return capitalizeString(
                  getHumanReadableDate(row?.expiry) || '',
                );
              },
            },
          ]}
          actions={[
            {
              id: '4',
              render: (row) => {
                return (
                  <>
                    <Actions
                      sections={[
                        {
                          items: [
                            <Actions.Item
                              key="remove-member"
                              title="Resend Invite"
                              icon="FeatherPlus"
                              onClick={() => {
                                setCurrentInvitation(row);
                                setDialogOpen('resend');
                              }}
                            />,
                            <Actions.Item
                              key="remove-member"
                              title="Cancel Invite"
                              icon="FeatherTrash"
                              onClick={() => {
                                setCurrentInvitation(row);
                                setDialogOpen('cancel');
                              }}
                            />,
                          ],
                        },
                      ]}
                    />
                  </>
                );
              },
            },
          ]}
        />
      ) : (
        <div className="flex-[1_0_0] h-full w-full flex-col gap-2 items-center justify-center flex">
          <span>No Invites Pending</span>
        </div>
      )}
      {dialogOpen === 'resend' && (
        <InviteTeamMemberDialog
          handleOpenChange={() => {
            refetchPendingMembers();
            setDialogOpen(undefined);
          }}
          open={dialogOpen === 'resend'}
          memberEmail={currentInvitation?.email}
        />
      )}
      {dialogOpen === 'cancel' && (
        <DeleteInvitationDialog
          setOpen={() => {
            refetchPendingMembers();
            setDialogOpen(undefined);
          }}
          open={dialogOpen === 'cancel'}
          invitation={currentInvitation}
        />
      )}
    </>
  );
}
